import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import { toast } from "sonner";
import { Mail, Info, CheckCircle, AlertCircle, Send, ChevronDown, ChevronUp, Globe, Sparkles } from "lucide-react";

const INDUSTRIES = ["Consumer Goods", "Retail", "Technology", "Finance", "Energy", "Healthcare", "Manufacturing", "Logistics", "Telecom", "Banking / Merchant Acquiring", "Insurance", "Telecommunications"];
const CURRENCIES = [
  { code: "USD", symbol: "$", label: "US Dollar" },
  { code: "EUR", symbol: "€", label: "Euro" },
  { code: "GBP", symbol: "£", label: "British Pound" },
  { code: "NGN", symbol: "₦", label: "Nigerian Naira" },
  { code: "ZAR", symbol: "R", label: "South African Rand" },
  { code: "KES", symbol: "KSh", label: "Kenyan Shilling" },
  { code: "GHS", symbol: "₵", label: "Ghanaian Cedi" },
  { code: "EGP", symbol: "£", label: "Egyptian Pound" },
  { code: "INR", symbol: "₹", label: "Indian Rupee" },
  { code: "BRL", symbol: "R$", label: "Brazilian Real" },
  { code: "MXN", symbol: "$", label: "Mexican Peso" },
  { code: "AED", symbol: "د.إ", label: "UAE Dirham" },
  { code: "SAR", symbol: "﷼", label: "Saudi Riyal" },
  { code: "JPY", symbol: "¥", label: "Japanese Yen" },
  { code: "CNY", symbol: "¥", label: "Chinese Yuan" },
  { code: "AUD", symbol: "A$", label: "Australian Dollar" },
  { code: "CAD", symbol: "C$", label: "Canadian Dollar" },
  { code: "SGD", symbol: "S$", label: "Singapore Dollar" },
];

export default function GeneralTab({ isOwner }) {
  const [ws, setWs] = useState(null);
  const [name, setName] = useState("");
  const [industry, setIndustry] = useState("");
  const [tier, setTier] = useState("enterprise");
  const [savingTier, setSavingTier] = useState(false);
  const [emailReplies, setEmailReplies] = useState(false);
  const [dailyBriefing, setDailyBriefing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [togglingReplies, setTogglingReplies] = useState(false);
  const [togglingBriefing, setTogglingBriefing] = useState(false);
  const [testingBriefing, setTestingBriefing] = useState(false);
  const [inboundStatus, setInboundStatus] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [showOps, setShowOps] = useState(false);
  // Phase 20: Analysis Context
  const [ctx, setCtx] = useState({ country: "", currency_code: "USD", currency_symbol: "$", business_model: "", target_segments: "", benchmarks: "" });
  const [savingCtx, setSavingCtx] = useState(false);

  const load = () => api.get("/workspace").then(r => {
    setWs(r.data);
    setName(r.data.name);
    setIndustry(r.data.industry);
    setTier(r.data.tier || "enterprise");
    setEmailReplies(Boolean(r.data.email_replies_enabled));
    setDailyBriefing(Boolean(r.data.daily_briefing_enabled));
    const ac = r.data.analysis_context || {};
    setCtx({
      country: ac.country || "",
      currency_code: ac.currency_code || "USD",
      currency_symbol: ac.currency_symbol || "$",
      business_model: ac.business_model || "",
      target_segments: ac.target_segments || "",
      benchmarks: ac.benchmarks || "",
    });
  });

  useEffect(() => {
    load();
    api.get("/inbound/status").then(r => setInboundStatus(r.data)).catch(() => setInboundStatus({ ready: false }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/workspace", { name, industry });
      toast.success("Workspace updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
    setSaving(false);
  };

  const saveTier = async (val) => {
    setSavingTier(true);
    try {
      await api.patch("/workspace", { tier: val });
      setTier(val);
      toast.success(
        val === "micro"
          ? "Workspace switched to Micro · SMB glance view active."
          : "Workspace switched to Enterprise · full terminal active."
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
    setSavingTier(false);
  };

  const toggleEmailReplies = async (val) => {
    setTogglingReplies(true);
    try {
      await api.patch("/workspace", { email_replies_enabled: val });
      setEmailReplies(val);
      toast.success(val ? "DARA Replies enabled." : "DARA Replies disabled.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
    setTogglingReplies(false);
  };

  const toggleDailyBriefing = async (val) => {
    setTogglingBriefing(true);
    try {
      await api.patch("/workspace", { daily_briefing_enabled: val });
      setDailyBriefing(val);
      toast.success(val ? "Daily Briefing enabled. First email arrives tomorrow at 08:00 UTC." : "Daily Briefing disabled.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
    setTogglingBriefing(false);
  };

  const sendBriefingTest = async () => {
    setTestingBriefing(true);
    try {
      const r = await api.post("/workspace/daily-briefing/test");
      if (r.data?.ok) {
        toast.success(`Test briefing sent to ${r.data.sent_to} (${r.data.preview_count} insights)`);
      } else {
        toast.message(`Briefing rendered (provider=${r.data?.provider}). Email may be in log mode.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    }
    setTestingBriefing(false);
  };

  const sendStarterEmail = async () => {
    setSeeding(true);
    try {
      const r = await api.post("/inbound/seed-thread");
      toast.success(`Starter email sent to ${r.data?.sent_to}. Reply to it with '?' + your question.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send starter email");
    }
    setSeeding(false);
  };

  const pickCurrency = (code) => {
    const c = CURRENCIES.find(x => x.code === code) || CURRENCIES[0];
    setCtx({ ...ctx, currency_code: c.code, currency_symbol: c.symbol });
  };

  const saveContext = async () => {
    setSavingCtx(true);
    try {
      await api.patch("/workspace", { analysis_context: ctx });
      toast.success("Analysis context saved. Next Analyze-with-DARA run will use it.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSavingCtx(false);
  };

  if (!ws) return null;

  const ready = inboundStatus?.ready;
  const active = ready && emailReplies;

  return (
    <div className="space-y-6">
      <Panel testId="general-tab" title="// WORKSPACE IDENTITY">
        <div className="p-6 space-y-5 max-w-xl">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Company name</label>
            <input
              data-testid="ws-name" value={name} onChange={e => setName(e.target.value)}
              disabled={!isOwner}
              className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000] disabled:opacity-50"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Industry</label>
            <select
              data-testid="ws-industry" value={industry} onChange={e => setIndustry(e.target.value)}
              disabled={!isOwner}
              className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000] disabled:opacity-50"
            >
              {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-4 pt-2">
            <div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600">MEMBERS</div>
              <div className="font-mono text-2xl text-white mt-1">{ws.member_count}</div>
            </div>
            <div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600">WORKSPACE ID</div>
              <div className="font-mono text-xs text-zinc-400 mt-1 truncate">{ws.id}</div>
            </div>
            <div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600">CREATED</div>
              <div className="font-mono text-xs text-zinc-400 mt-1">{new Date(ws.created_at).toLocaleDateString()}</div>
            </div>
          </div>
          {isOwner && (
            <button
              onClick={save}
              disabled={saving}
              data-testid="save-workspace-btn"
              style={{ backgroundColor: "var(--brand-accent, #FFB000)", color: "var(--brand-accent-text, #000)" }}
              className="font-bold font-mono text-[11px] uppercase tracking-wider px-5 py-2.5 hover:opacity-90 disabled:opacity-50 transition-opacity"
            >{saving ? "SAVING…" : "SAVE CHANGES"}</button>
          )}
        </div>
      </Panel>

      <Panel testId="workspace-tier-section" title="// WORKSPACE TIER">
        <div className="p-6 space-y-4 max-w-2xl">
          <div className="flex items-start gap-3">
            <Info className="w-4 h-4 text-zinc-500 mt-1 flex-shrink-0" />
            <p className="text-sm text-zinc-400 leading-relaxed">
              Choose how POS Live renders for this workspace. Enterprise keeps the full Bloomberg-style
              terminal. <strong className="text-white">Micro</strong> swaps it for a glance-first SMB view
              — hero revenue card, watch-outs, one-line DARA pulse — designed for owners checking their
              phone between customers.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={() => isOwner && tier !== "enterprise" && saveTier("enterprise")}
              disabled={!isOwner || savingTier}
              data-testid="tier-enterprise"
              className={`text-left p-4 border transition-colors ${
                tier === "enterprise"
                  ? "border-[#FFB000] bg-[#FFB000]/10"
                  : "border-zinc-800 hover:border-zinc-700"
              } disabled:opacity-50`}
            >
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] flex items-center gap-1.5">
                {tier === "enterprise" && <CheckCircle className="w-3 h-3" />}
                ENTERPRISE
              </div>
              <div className="font-heading font-bold text-base mt-1">Full terminal</div>
              <div className="font-mono text-[10px] text-zinc-500 mt-1 leading-relaxed">
                KPI strip · hourly chart · payment-mix donut · top SKUs · live tail · multi-driver brief.
              </div>
            </button>

            <button
              onClick={() => isOwner && tier !== "micro" && saveTier("micro")}
              disabled={!isOwner || savingTier}
              data-testid="tier-micro"
              className={`text-left p-4 border transition-colors ${
                tier === "micro"
                  ? "border-[#FFB000] bg-[#FFB000]/10"
                  : "border-zinc-800 hover:border-zinc-700"
              } disabled:opacity-50`}
            >
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] flex items-center gap-1.5">
                {tier === "micro" && <CheckCircle className="w-3 h-3" />}
                MICRO · SMB
              </div>
              <div className="font-heading font-bold text-base mt-1">Glance view</div>
              <div className="font-mono text-[10px] text-zinc-500 mt-1 leading-relaxed">
                Hero revenue · vs same-day-last-week · watch-outs (dead windows, voids) · one-line pulse.
              </div>
            </button>
          </div>
          {!isOwner && (
            <div className="font-mono text-[10px] text-zinc-600">Only workspace owners can change tier.</div>
          )}
        </div>
      </Panel>

      <Panel testId="analysis-context-section" title="// ANALYSIS CONTEXT (FEEDS DARA)" meta={<Globe className="w-3.5 h-3.5 text-[#FFB000]" />}>
        <div className="p-6 space-y-5 max-w-3xl">
          <div className="flex items-start gap-3 mb-2">
            <Sparkles className="w-4 h-4 text-[#FFB000] mt-1 flex-shrink-0" />
            <p className="text-sm text-zinc-400 leading-relaxed">
              Tell DARA <strong className="text-white">who you are</strong> so DARA analyses your data through the right lens —
              correct country, currency, industry, and competitive benchmarks. Without this, insights default to US-retail framing
              and dollar figures that won&apos;t relate to your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Country / market</label>
              <input
                data-testid="ctx-country"
                value={ctx.country}
                onChange={e => setCtx({ ...ctx, country: e.target.value })}
                placeholder="e.g. Nigeria"
                disabled={!isOwner}
                className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000] disabled:opacity-50"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Currency</label>
              <select
                data-testid="ctx-currency"
                value={ctx.currency_code}
                onChange={e => pickCurrency(e.target.value)}
                disabled={!isOwner}
                className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000] disabled:opacity-50"
              >
                {CURRENCIES.map(c => (
                  <option key={c.code} value={c.code}>{c.symbol}  {c.code} — {c.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Business model</label>
            <input
              data-testid="ctx-business-model"
              value={ctx.business_model}
              onChange={e => setCtx({ ...ctx, business_model: e.target.value.slice(0, 280) })}
              placeholder="e.g. Merchant acquiring + SME banking across 5 Nigerian cities"
              disabled={!isOwner}
              className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000] disabled:opacity-50"
            />
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Target segments</label>
            <input
              data-testid="ctx-target-segments"
              value={ctx.target_segments}
              onChange={e => setCtx({ ...ctx, target_segments: e.target.value.slice(0, 280) })}
              placeholder="e.g. SMEs, retailers, restaurants, enterprise"
              disabled={!isOwner}
              className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000] disabled:opacity-50"
            />
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              Local benchmarks / regulators to cite
              <span className="ml-2 text-zinc-600">optional</span>
            </label>
            <input
              data-testid="ctx-benchmarks"
              value={ctx.benchmarks}
              onChange={e => setCtx({ ...ctx, benchmarks: e.target.value.slice(0, 280) })}
              placeholder="e.g. CBN, EFInA, NIBSS (Nigeria) / RBI, NPCI (India) / BACEN (Brazil)"
              disabled={!isOwner}
              className="mt-2 w-full bg-black border border-zinc-800 px-3 py-2.5 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000] disabled:opacity-50"
            />
          </div>

          {isOwner && (
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={saveContext}
                disabled={savingCtx}
                data-testid="save-context-btn"
                style={{ backgroundColor: "var(--brand-accent, #FFB000)", color: "var(--brand-accent-text, #000)" }}
                className="font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2.5 hover:opacity-90 disabled:opacity-50 transition-opacity"
              >{savingCtx ? "SAVING…" : "SAVE ANALYSIS CONTEXT"}</button>
              <div className="font-mono text-[10px] text-zinc-600">
                Applied on every <span className="text-[#FFB000]">ANALYZE WITH DARA</span> run, in the background for Monday briefings, and via DARA Replies.
              </div>
            </div>
          )}
        </div>
      </Panel>

      <Panel testId="dara-replies-section" title="// DARA REPLIES (EMAIL)" meta={<Mail className="w-3.5 h-3.5" />}>
        <div className="p-6 max-w-2xl">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 border border-[#FFB000]/40 bg-[#FFB000]/10 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-[#FFB000]" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-bold text-lg mb-2">Ask DARA from your inbox.</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">
                DARA doesn&apos;t expose a public inbox — you <strong className="text-white">reply</strong> to any email DARA sends you
                (Monday briefing, walkthrough alert, hot-lead notification) and start your reply with a
                question mark <code className="text-[#FFB000] bg-zinc-900 px-1.5 py-0.5">?</code>. DARA grounds the answer in this workspace&apos;s data
                and emails the reply back within ~20 seconds.
              </p>
              <div className="mt-3 font-mono text-[11px] text-zinc-500 bg-zinc-900/60 border-l-2 border-[#FFB000] p-3 space-y-1">
                <div>EXAMPLES of a reply line:</div>
                <div className="text-zinc-300">?what&apos;s driving the APAC revenue dip this week?</div>
                <div className="text-zinc-300">?which 3 insights should I share with the board on Monday?</div>
              </div>
            </div>
          </div>

          {/* STATUS STRIP — now driven by real backend check, not just the toggle */}
          <div className="flex items-center justify-between p-4 border border-zinc-800 mt-4">
            <div className="flex items-center gap-3">
              {active
                ? <CheckCircle className="w-5 h-5 text-[#00E676]" />
                : ready
                  ? <AlertCircle className="w-5 h-5 text-[#FFB000]" />
                  : <AlertCircle className="w-5 h-5 text-zinc-600" />
              }
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">STATUS</div>
                <div className={`font-mono text-sm mt-0.5 ${active ? "text-[#00E676]" : ready ? "text-[#FFB000]" : "text-zinc-500"}`} data-testid="dara-replies-status">
                  {active ? "● LIVE · replies arrive within ~20s" : ready ? "○ READY · owner has not enabled yet" : "○ INFRASTRUCTURE NOT CONFIGURED"}
                </div>
              </div>
            </div>
            {isOwner && ready && (
              <button
                onClick={() => toggleEmailReplies(!emailReplies)}
                disabled={togglingReplies}
                data-testid="toggle-email-replies"
                style={!emailReplies ? { backgroundColor: "var(--brand-accent, #FFB000)", color: "var(--brand-accent-text, #000)" } : undefined}
                className={`font-mono text-[11px] uppercase tracking-widest px-5 py-2.5 disabled:opacity-50 transition-opacity ${
                  emailReplies
                    ? "border border-zinc-700 hover:border-[#FF3B30]/50 hover:text-[#FF3B30]"
                    : "font-bold hover:opacity-90"
                }`}
              >{togglingReplies ? "SAVING…" : emailReplies ? "TURN OFF" : "TURN ON"}</button>
            )}
          </div>

          {/* STARTER-THREAD CTA — solves the "I have nothing to reply to yet" gap */}
          {active && (
            <div className="flex items-center justify-between p-4 border border-[#FFB000]/30 bg-[#FFB000]/5 mt-3">
              <div className="min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">
                  FIRST TIME? BOOTSTRAP A THREAD
                </div>
                <div className="font-mono text-[11px] text-zinc-400 mt-1">
                  Emails it to <span className="text-white">{ws.owner_email || "you"}</span>. Reply to it with <code className="text-[#FFB000]">?</code>+question — no Monday briefing needed.
                </div>
              </div>
              <button
                onClick={sendStarterEmail}
                disabled={seeding}
                data-testid="seed-thread-btn"
                className="border border-[#FFB000] text-[#FFB000] hover:bg-[#FFB000]/10 font-mono text-[11px] uppercase tracking-widest px-4 py-2.5 disabled:opacity-50 flex items-center gap-2 flex-shrink-0 ml-3"
              >
                <Send className="w-3.5 h-3.5" />
                {seeding ? "SENDING…" : "EMAIL ME A STARTER"}
              </button>
            </div>
          )}

          {/* Collapsible operator note — only for owners, only when relevant */}
          {isOwner && (
            <button
              onClick={() => setShowOps(v => !v)}
              data-testid="toggle-ops-note"
              className="mt-4 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-600 hover:text-[#FFB000]"
            >
              <Info className="w-3 h-3" />
              OPERATOR SETUP NOTE
              {showOps ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          )}
          {isOwner && showOps && (
            <div className="mt-2 p-4 border border-zinc-900 bg-zinc-950 font-mono text-[11px] text-zinc-500 space-y-2 leading-relaxed" data-testid="ops-note">
              <div>
                <span className="text-zinc-400">Signing secret:</span>{" "}
                <span className={inboundStatus?.signing_secret_configured ? "text-[#00E676]" : "text-[#FF3B30]"}>
                  {inboundStatus?.signing_secret_configured ? "configured ✓" : "not set ✗"}
                </span>
              </div>
              <div>
                <span className="text-zinc-400">Webhook URL:</span>{" "}
                <span className="text-zinc-300">{inboundStatus?.webhook_url || "/api/inbound/email"}</span>
              </div>
              <div>
                <span className="text-zinc-400">Outbound from:</span>{" "}
                <span className="text-zinc-300">{inboundStatus?.from_email || "—"}</span>
              </div>
              <div className="pt-2 border-t border-zinc-900 text-zinc-600">
                To enable end-to-end: set <code className="text-[#FFB000]">RESEND_INBOUND_SIGNING_SECRET</code> in{" "}
                <code>backend/.env</code>, then in Resend dashboard add an inbound webhook pointing at{" "}
                <code>{inboundStatus?.webhook_url || "/api/inbound/email"}</code> with event <code>email.received</code>.
                Restart backend.
              </div>
            </div>
          )}
        </div>
      </Panel>

      <Panel testId="daily-briefing-section" title="// DAILY MORNING BRIEFING" meta={<Mail className="w-3.5 h-3.5" />}>
        <div className="p-6 max-w-2xl">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 border border-[#00E676]/40 bg-[#00E676]/10 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-[#00E676]" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-bold text-lg mb-2">A short morning email. The 3 things that matter today.</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">
                When this is on, every weekday at <span className="text-white">08:00 UTC</span> DARA emails
                you the same top-3 insights you&apos;d see on the Today page. Designed for operators who don&apos;t want to log in every day —
                they read the email, action what matters, click <span className="text-[#FFB000]">Review in DARA</span> for anything else.
              </p>
              <div className="mt-3 font-mono text-[11px] text-zinc-500 bg-zinc-900/60 border-l-2 border-[#00E676] p-3 leading-relaxed">
                Sent to the workspace owner&apos;s email. One email per workspace. Quiet days get an &quot;all clear&quot; note instead of a forced summary.
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 border border-zinc-800 mt-4">
            <div className="flex items-center gap-3">
              {dailyBriefing ? <CheckCircle className="w-5 h-5 text-[#00E676]" /> : <AlertCircle className="w-5 h-5 text-zinc-600" />}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">STATUS</div>
                <div className={`font-mono text-sm mt-0.5 ${dailyBriefing ? "text-[#00E676]" : "text-zinc-500"}`} data-testid="daily-briefing-status">
                  {dailyBriefing ? "● ACTIVE · next email tomorrow 08:00 UTC" : "○ DISABLED"}
                </div>
              </div>
            </div>
            {isOwner && (
              <button
                onClick={() => toggleDailyBriefing(!dailyBriefing)}
                disabled={togglingBriefing}
                data-testid="toggle-daily-briefing"
                style={!dailyBriefing ? { backgroundColor: "var(--brand-accent, #FFB000)", color: "var(--brand-accent-text, #000)" } : undefined}
                className={`font-mono text-[11px] uppercase tracking-widest px-5 py-2.5 disabled:opacity-50 transition-opacity ${
                  dailyBriefing
                    ? "border border-zinc-700 hover:border-[#FF3B30]/50 hover:text-[#FF3B30]"
                    : "font-bold hover:opacity-90"
                }`}
              >{togglingBriefing ? "SAVING…" : dailyBriefing ? "TURN OFF" : "TURN ON"}</button>
            )}
          </div>

          {isOwner && dailyBriefing && (
            <div className="flex items-center justify-between p-4 border border-[#00E676]/30 bg-[#00E676]/5 mt-3">
              <div className="min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#00E676]">
                  PREVIEW THE EMAIL
                </div>
                <div className="font-mono text-[11px] text-zinc-400 mt-1">
                  Sends a one-off briefing to your inbox right now so you can see what the cron will deliver.
                </div>
              </div>
              <button
                onClick={sendBriefingTest}
                disabled={testingBriefing}
                data-testid="test-daily-briefing-btn"
                className="font-mono text-[11px] uppercase tracking-widest px-4 py-2 border border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/10 disabled:opacity-50 flex items-center gap-2"
              >
                {testingBriefing ? "SENDING…" : "SEND TEST"}
              </button>
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}
